import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Pencil, Plus } from "lucide-react";

import {
  invoiceCreationBlockedTooltipKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import { recurringInvoiceKeys } from "@/lib/queryKeys";
import { listRecurringSchedules } from "@/services/recurringInvoiceService";

export function RecurringInvoicesListPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const pagination = usePaginationFromUrl();
  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();
  const disabledTooltipKey = invoiceCreationBlockedTooltipKey(invoicingBlockedReason);

  const listParams = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
    }),
    [pagination.page, pagination.limit]
  );

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: recurringInvoiceKeys.list(listParams),
    queryFn: () => listRecurringSchedules(listParams),
  });

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("recurringInvoices.list.error"));
    }
  }, [isError, t]);

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("recurringInvoices.list.title")}
        description={t("recurringInvoices.list.subtitle")}
        actions={
          invoicingBlocked && disabledTooltipKey ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button type="button" disabled aria-disabled="true">
                    <Plus className="mr-2 h-4 w-4" />
                    {t("recurringInvoices.list.add")}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t(disabledTooltipKey)}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button asChild className="transition-colors duration-150">
              <Link to="/invoices/recurring/new">
                <Plus className="mr-2 h-4 w-4" />
                {t("recurringInvoices.list.add")}
              </Link>
            </Button>
          )
        }
      />

      <TableCard>
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={t("recurringInvoices.list.emptyTitle")}
            message={t("recurringInvoices.list.emptyMessage")}
            action={
              invoicingBlocked && disabledTooltipKey ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button type="button" variant="default" disabled aria-disabled="true">
                        {t("recurringInvoices.list.add")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t(disabledTooltipKey)}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button asChild variant="default">
                  <Link to="/invoices/recurring/new">{t("recurringInvoices.list.add")}</Link>
                </Button>
              )
            }
          />
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-3 py-2 md:hidden">
              {rows.map((row) => {
                const clientLabel =
                  row.client?.displayName?.trim() ||
                  row.client?.legalName ||
                  "—";
                const actions: TableAction[] = [
                  {
                    label: t("recurringInvoices.list.edit"),
                    icon: Pencil,
                    onClick: () => navigate(`/invoices/recurring/${row.id}/edit`),
                  },
                ];
                return (
                  <div
                    key={row.id}
                    className="cursor-pointer rounded-lg border bg-card p-4 transition-colors duration-150 hover:bg-muted/50"
                    onClick={() => navigate(`/invoices/recurring/${row.id}/edit`)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 space-y-1">
                        <p className="font-medium text-foreground">{clientLabel}</p>
                        <StatusBadge
                          variant={row.isActive ? "success" : "neutral"}
                          className="inline-flex text-xs font-medium"
                        >
                          {row.isActive
                            ? t("recurringInvoices.status.active")
                            : t("recurringInvoices.status.inactive")}
                        </StatusBadge>
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <TableActionsMenu actions={actions} />
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>
                        {row.frequency === "monthly"
                          ? t("recurringInvoices.frequency.monthly")
                          : t("recurringInvoices.frequency.once")}
                      </span>
                      <span>
                        {formatDisplayDate(row.nextSendDate, {
                          locale: i18n.language,
                          useCalendarDate: true,
                        })}
                      </span>
                      <span className="tabular-nums">
                        {row.currency}{" "}
                        {row.amount.toLocaleString(i18n.language, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("recurringInvoices.list.columnClient")}</TableHead>
                  <TableHead>{t("recurringInvoices.list.columnNextSend")}</TableHead>
                  <TableHead>{t("recurringInvoices.list.columnFrequency")}</TableHead>
                  <TableHead className="text-right">{t("recurringInvoices.list.columnAmount")}</TableHead>
                  <TableHead className="text-center">
                    {t("recurringInvoices.list.columnStatus")}
                  </TableHead>
                  <TableHead className="w-[60px]" aria-hidden />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const clientLabel =
                    row.client?.displayName?.trim() ||
                    row.client?.legalName ||
                    "—";
                  const actions: TableAction[] = [
                    {
                      label: t("recurringInvoices.list.edit"),
                      icon: Pencil,
                      onClick: () => navigate(`/invoices/recurring/${row.id}/edit`),
                    },
                  ];
                  return (
                    <TableRow
                      key={row.id}
                      onRowClick={() => navigate(`/invoices/recurring/${row.id}/edit`)}
                    >
                      <TableCell className="font-medium text-foreground">
                        {clientLabel}
                      </TableCell>
                      <TableCell>
                        {formatDisplayDate(row.nextSendDate, {
                          locale: i18n.language,
                          useCalendarDate: true,
                        })}
                      </TableCell>
                      <TableCell>
                        {row.frequency === "monthly"
                          ? t("recurringInvoices.frequency.monthly")
                          : t("recurringInvoices.frequency.once")}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.currency}{" "}
                        {row.amount.toLocaleString(i18n.language, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell className="text-center align-middle">
                        <StatusBadge
                          variant={row.isActive ? "success" : "neutral"}
                          className="inline-flex text-xs font-medium"
                        >
                          {row.isActive
                            ? t("recurringInvoices.status.active")
                            : t("recurringInvoices.status.inactive")}
                        </StatusBadge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <TableActionsMenu actions={actions} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("recurringInvoices.list.rowsPerPage")}
              pageOfLabel={t("recurringInvoices.list.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
